/**
 * NotificationCenter.js — Notification Panel & Toast Components
 *
 * Exports:
 *   - NotificationPanel : dropdown attached to the bell button
 *   - NotificationToast : fixed bottom-right stacked toasts (auto-dismiss)
 */

import React, { useEffect, useState } from 'react';
import { X, Bell, AlertTriangle, Info, AlertCircle, CheckCircle, Megaphone } from 'lucide-react';
import { Modal } from './shared';
import { fleetApi } from '../api/fleetApi';
import { GEOFENCE_EVENT_COLORS } from '../constants/palette';

// ── Helpers ──────────────────────────────────────────────────────────────────

function timeAgo(timestamp) {
  const diff = Date.now() - new Date(timestamp).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function SeverityIcon({ severity, size = 16 }) {
  if (severity === 'danger')  return <AlertCircle   size={size} style={{ color: 'var(--danger)',  flexShrink: 0 }} />;
  if (severity === 'warning') return <AlertTriangle  size={size} style={{ color: 'var(--warning)', flexShrink: 0 }} />;
  if (severity === 'success') return <CheckCircle    size={size} style={{ color: 'var(--success)', flexShrink: 0 }} />;
  return                              <Info           size={size} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />;
}

const CATEGORY_LABELS = {
  compliance:   'Compliance',
  maintenance:  'Maintenance',
  operational:  'Operational',
  tracking:     'Tracking',
  behavior:     'Driver Behavior',
  geofence:     'Geofence',
};

/**
 * Enter and Exit must never look alike: a vehicle arriving at a depot and one
 * leaving it call for opposite responses, and severity alone cannot separate
 * them — the API sends both as Info.
 */
const geofenceDirection = (type) => {
  if (/^GeofenceEnter$/i.test(type || '')) return { label: 'Entered', color: GEOFENCE_EVENT_COLORS.Enter.text };
  if (/^GeofenceExit$/i.test(type  || '')) return { label: 'Left',    color: GEOFENCE_EVENT_COLORS.Exit.text };
  return null;
};

// ── NotificationPanel ─────────────────────────────────────────────────────────

export function NotificationPanel({ notifications, unreadCount, hasMore, loading, onLoadMore, onMarkRead, onMarkAllRead, onDeleteNotif, onClose, onNavigate }) {
  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest('.notif-panel-wrapper')) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  return (
    <div className="notif-panel-wrapper">
      <div className="notif-panel">
        {/* Header */}
        <div className="notif-panel-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Bell size={16} />
            <span style={{ fontWeight: 600, fontSize: 14 }}>Notifications</span>
            {unreadCount > 0 && (
              <span style={{
                background: 'var(--danger)', color: '#fff',
                borderRadius: 8, fontSize: 10, fontWeight: 600,
                padding: '1px 6px',
              }}>{unreadCount}</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {unreadCount > 0 && (
              <button className="notif-mark-read-btn" onClick={onMarkAllRead}>
                Mark all read
              </button>
            )}
            <button className="notif-close-btn" onClick={onClose}><X size={14} /></button>
          </div>
        </div>

        {/* List */}
        <div className="notif-list">
          {loading ? (
            <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[1, 2, 3].map(i => (
                <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'var(--bg-elevated)', flexShrink: 0 }} className="skeleton-pulse" />
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ height: 12, borderRadius: 4, background: 'var(--bg-elevated)', width: '60%' }} className="skeleton-pulse" />
                    <div style={{ height: 10, borderRadius: 4, background: 'var(--bg-elevated)', width: '90%' }} className="skeleton-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : notifications.length === 0 ? (
            <div className="notif-empty">
              <Bell size={32} style={{ color: 'var(--text-muted)', marginBottom: 8 }} />
              <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No notifications</div>
            </div>
          ) : (
            notifications.map(n => {
            // An alert that names a vehicle can be opened on the map. Anything
            // else keeps the old behaviour: clicking only marks it read.
            const target = (onNavigate && n.relatedEntityType === 'Vehicle' && n.relatedEntityId != null)
              ? `/tracking?vehicleId=${encodeURIComponent(n.relatedEntityId)}`
              : null;
            const direction = geofenceDirection(n.type);
            return (
              <div
                key={n.id}
                className={`notif-item ${n.read ? 'read' : 'unread'} ${n.severity}`}
                onClick={() => {
                  if (!n.read) onMarkRead(n.id);
                  if (target) { onNavigate(target); onClose(); }
                }}
                style={{ cursor: (target || !n.read) ? 'pointer' : 'default' }}
              >
                <SeverityIcon severity={n.severity} size={15} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>{n.title}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                        {timeAgo(n.timestamp)}
                      </div>
                      <button
                        onClick={e => { e.stopPropagation(); onDeleteNotif(n.id); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 2, display: 'flex', alignItems: 'center', opacity: 0.6 }}
                        title="Delete"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{n.message}</div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4 }}>
                    <span style={{
                      fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em',
                      color: n.severity === 'warning' ? '#f97316'
                           : n.severity === 'danger'  ? 'var(--danger)'
                           : n.severity === 'success' ? 'var(--success)'
                           : 'var(--text-muted)',
                    }}>
                      {n.severity === 'warning' ? 'Warning' : n.severity === 'danger' ? 'Critical' : n.severity === 'success' ? 'Success' : 'Info'}
                    </span>
                    <span style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {CATEGORY_LABELS[n.category] || n.category}
                    </span>
                    {direction && (
                      <span style={{
                        fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
                        color: direction.color, display: 'inline-flex', alignItems: 'center', gap: 4,
                      }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: direction.color, display: 'inline-block' }} />
                        {direction.label}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
            })
          )}
          {hasMore && (
            <button className="notif-load-more-btn" onClick={onLoadMore}>
              Load more
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── NotificationToast ─────────────────────────────────────────────────────────

// ── AnnouncementModal ──────────────────────────────────────────────────────────

const TARGET_TYPES = [
  { value: 'all',   label: 'All Users' },
  { value: 'user',  label: 'Specific User' },
  { value: 'fleet', label: 'Fleet' },
  { value: 'zone',  label: 'Zone' },
];

const SEVERITY_OPTIONS = [
  { value: 1, label: '1 — Info' },
  { value: 2, label: '2 — Warning' },
  { value: 3, label: '3 — High' },
  { value: 4, label: '4 — Critical' },
];

export function AnnouncementModal({ onClose, authToken, currentUser, onToast }) {
  const orgId = currentUser?.organizationId || 0;

  const [targetType, setTargetType] = useState('all');
  const [targetId, setTargetId]     = useState('');
  const [options, setOptions]       = useState([]);
  const [title, setTitle]           = useState('');
  const [message, setMessage]       = useState('');
  const [notifType, setNotifType]   = useState('Announcement');
  const [severity, setSeverity]     = useState(1);
  const [sendToWebhook, setSendToWebhook] = useState(false);
  const [loading, setLoading]       = useState(false);
  const [errors, setErrors]         = useState({});

  // Fetch the dropdown list whenever targetType changes
  useEffect(() => {
    if (targetType === 'all') { setOptions([]); setTargetId(''); return; }
    setOptions([]);
    setTargetId('');
    let cancelled = false;

    const fetch = async () => {
      try {
        let data = [];
        if (targetType === 'user') {
          const raw = await fleetApi.users.getListByOrganizationId(orgId, authToken);
          data = (raw || []).map(u => ({ id: u.id, label: u.fullName || u.username || u.email }));
        } else if (targetType === 'fleet') {
          const raw = await fleetApi.fleets.getAll(orgId, authToken);
          data = (raw || []).map(f => ({ id: f.id, label: f.name }));
        } else if (targetType === 'zone') {
          const raw = await fleetApi.zones.getList(orgId, authToken);
          data = (raw || []).map(z => ({ id: z.id, label: z.name }));
        }
        if (!cancelled) setOptions(data);
      } catch {
        if (!cancelled) setOptions([]);
      }
    };
    fetch();
    return () => { cancelled = true; };
  }, [targetType, orgId, authToken]);

  const validate = () => {
    const e = {};
    if (!title.trim())   e.title   = 'Title is required';
    if (!message.trim()) e.message = 'Message is required';
    if (targetType !== 'all' && !targetId) e.targetId = 'Please select a target';
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setLoading(true);
    try {
      await fleetApi.notifications.sendAnnouncement({
        fleetId:          targetType === 'fleet' ? Number(targetId) : 0,
        zoneId:           targetType === 'zone'  ? Number(targetId) : 0,
        organizationId:   orgId,
        userId:           targetType === 'user'  ? Number(targetId) : 0,
        title:            title.trim(),
        message:          message.trim(),
        notificationType: notifType,
        severity:         Number(severity),
        sendToWebhook,
      }, authToken);
      onToast({ title: 'Announcement sent', message: 'Your message has been broadcast successfully.', severity: 'success' });
      onClose();
    } catch (err) {
      onToast({ title: 'Failed to send', message: err.message || 'Something went wrong.', severity: 'danger' });
    } finally {
      setLoading(false);
    }
  };

  const clearError = (field) => setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });

  return (
    <Modal
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Megaphone size={18} />
          Send Announcement
        </span>
      }
      onClose={onClose}
      size="md"
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={loading}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
            {loading ? 'Sending…' : 'Send'}
          </button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* Target type */}
        <div className="form-group">
          <label className="form-label">Send To</label>
          <select
            className="form-select"
            value={targetType}
            onChange={e => { setTargetType(e.target.value); clearError('targetId'); }}
          >
            {TARGET_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>

        {/* Conditional target dropdown */}
        {targetType !== 'all' && (
          <div className="form-group">
            <label className="form-label">
              {targetType === 'user' ? 'User' : targetType === 'fleet' ? 'Fleet' : 'Zone'}
            </label>
            <select
              className={`form-select${errors.targetId ? ' input-error' : ''}`}
              value={targetId}
              onChange={e => { setTargetId(e.target.value); clearError('targetId'); }}
            >
              <option value="">— Select —</option>
              {options.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
            {errors.targetId && <span className="field-error">{errors.targetId}</span>}
          </div>
        )}

        {/* Title */}
        <div className="form-group">
          <label className="form-label">Title</label>
          <input
            className={`form-input${errors.title ? ' input-error' : ''}`}
            placeholder="Announcement title"
            value={title}
            onChange={e => { setTitle(e.target.value); clearError('title'); }}
          />
          {errors.title && <span className="field-error">{errors.title}</span>}
        </div>

        {/* Message */}
        <div className="form-group">
          <label className="form-label">Message</label>
          <textarea
            className={`form-input${errors.message ? ' input-error' : ''}`}
            placeholder="Write your message…"
            rows={4}
            style={{ resize: 'vertical' }}
            value={message}
            onChange={e => { setMessage(e.target.value); clearError('message'); }}
          />
          {errors.message && <span className="field-error">{errors.message}</span>}
        </div>

        <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {/* Notification type */}
          <div className="form-group">
            <label className="form-label">Type</label>
            <input
              className="form-input"
              value="Announcement"
              disabled
            />
          </div>

          {/* Severity */}
          <div className="form-group">
            <label className="form-label">Severity</label>
            <select className="form-select" value={severity} onChange={e => setSeverity(e.target.value)}>
              {SEVERITY_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
        </div>

        {/* Webhook */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox"
            id="ann-webhook"
            checked={sendToWebhook}
            onChange={e => setSendToWebhook(e.target.checked)}
            style={{ width: 16, height: 16, cursor: 'pointer' }}
          />
          <label htmlFor="ann-webhook" style={{ fontSize: 13, cursor: 'pointer', userSelect: 'none' }}>
            Send to WhatsApp
          </label>
        </div>

      </div>
    </Modal>
  );
}

// ── NotificationToast ─────────────────────────────────────────────────────────

export function NotificationToast({ toasts, onDismiss }) {
  useEffect(() => {
    if (!toasts.length) return;
    // Auto-dismiss the oldest visible toast after 5s
    const timer = setTimeout(() => onDismiss(toasts[toasts.length - 1].id), 5000);
    return () => clearTimeout(timer);
  }, [toasts, onDismiss]);

  if (!toasts.length) return null;

  // Show max 3 at once
  const visible = toasts.slice(-3);

  return (
    <div className="toast-container">
      {visible.map(toast => (
        <div key={toast.id} className={`toast ${toast.severity}`}>
          <SeverityIcon severity={toast.severity} size={18} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', marginBottom: 2 }}>
              {toast.title}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{toast.message}</div>
          </div>
          <button
            onClick={() => onDismiss(toast.id)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', flexShrink: 0, padding: 2 }}
          >
            <X size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}
